/**
 * Anzeige-Logik des Vertragsbereichs: Status als Pille, Stand je Unterzeichner, nächster Schritt, Dringlichkeit,
 * Filter und Kennzahlen der Liste. Reine Funktionen, getestet in test/vertraege-modell.test.ts.
 */
import { confidentialPartNoteText, finalHashInput, minorStatus, type ContractRole } from "@talentkontor/domain";
import type { Pill } from "../../../model/invoice-view";
import type { ContractListRow, ContractStatus, SignerRow, VoidKind } from "./daten";
import { currentLang, t, ta } from "../../../lib/sprache";

// Creator-Konto (0178/0197): die Rolle agency ist das Konto selbst, im Vertragstext „Creator“
export const ROLE_LABEL: Record<ContractRole, string> = { get agency() { return ta("Agentur", "Agency", "Creator", "Creator"); }, get brand() { return t("Marke", "Brand"); }, talent: "Creator" };
export const ROLE_ORDER: ContractRole[] = ["brand", "talent", "agency"];

/**
 * Wer den vertraulichen Teil sieht, in der Sprache der Oberfläche (VB-01; bisher „Anlage verbergen vor“), etwa „Nur für
 * Marke und Agentur sichtbar. Der Creator sieht diesen Teil nicht.“ Dieselben Sätze stehen beim Versand in der Kopfzeile
 * des Teils (contract_send, 0111), dort in der Sprache des Vertrags.
 */
export function vertraulichKreis(hidden: readonly ContractRole[], parties: readonly ContractRole[]): string {
  const n = confidentialPartNoteText(currentLang(), hidden, parties);
  return n ? `${n.visible} ${n.hidden}` : t("Alle Parteien sehen diesen Teil.", "All parties see this part.");
}

export const STATUS_PILL: Record<ContractStatus, Pill> = {
  draft: { get label() { return t("Entwurf", "Draft"); }, tone: "neutral", symbol: "○" },
  sent: { get label() { return t("Zur Unterschrift", "Awaiting signature"); }, tone: "info", symbol: "◇" },
  partially_signed: { get label() { return t("Teilweise unterschrieben", "Partially signed"); }, tone: "warning", symbol: "◷" },
  signed: { get label() { return t("Unterschrieben", "Signed"); }, tone: "success", symbol: "✓" },
  void: { get label() { return t("Aufgehoben", "Rescinded"); }, tone: "neutral", symbol: "○" },
};

export const VOID_KIND_LABEL: Record<VoidKind, string> = {
  get withdrawn() { return t("Zurückgezogen", "Withdrawn"); }, get error() { return t("Irrtümlich erfasst", "Recorded by mistake"); }, get mutual() { return t("Einvernehmlich aufgehoben", "Mutually rescinded"); }, get superseded() { return t("Durch neue Fassung ersetzt", "Replaced by a new version"); },
};

/**
 * Befristet ohne Verlängerung und das Ende liegt zurück (VERTRAEGE-R3-01): Der Vertrag ist ausgelaufen, ohne dass es einer
 * Kündigung bedurfte. Wie app.contract_fixed_end in der Datenbank; eine Kündigung setzt ends_on und beendet die Verlängerung.
 */
export function ausgelaufen(c: { status: ContractStatus; ends_on?: string | null; auto_renew?: boolean | null }, today?: string | null): boolean {
  return c.status === "signed" && !!today && !!c.ends_on && !c.auto_renew && c.ends_on.slice(0, 10) < today.slice(0, 10);
}

/**
 * Offener Vertrag, dessen Creator am Tag der Unterschrift minderjährig ist, ohne gesetzliche Vertretung als Unterzeichner
 * (CREATOR-R3-2, wie app.contract_minor_without_guardian): Die Datenbank sperrt dann jede Unterschrift, auch die Gegenzeichnung.
 */
export function minderjaehrigOhneVertretung(c: { status: ContractStatus; origin: string | null; required_signers: readonly ContractRole[]; starts_on: string | null },
  birthDate: string | null | undefined, signers: readonly { role: ContractRole; capacity: "party" | "legal_guardian" }[], today: string): boolean {
  if ((c.status !== "sent" && c.status !== "partially_signed") || c.origin !== "portal" || !c.required_signers.includes("talent")) return false;
  if (signers.some((s) => s.role === "talent" && s.capacity === "legal_guardian")) return false;
  return minorStatus(birthDate ?? null, c.starts_on ?? today, null, today).minor;
}

/** Status mit den Zuständen, die der Datenbankstatus allein nicht zeigt (abgelehnt, gekündigt, ausgelaufen, Art der Aufhebung, PDF fehlt). */
export function statusPill(c: Pick<ContractListRow, "status" | "declined_at" | "terminated_on" | "void_kind" | "origin"> & { ends_on?: string | null; auto_renew?: boolean | null }, today?: string): Pill {
  if (c.origin === "pdf_upload" && c.status === "draft") return { label: t("PDF fehlt", "PDF missing"), tone: "error", symbol: "!" };
  if ((c.status === "sent" || c.status === "partially_signed") && c.declined_at) return { label: t("Abgelehnt, angehalten", "Declined, on hold"), tone: "error", symbol: "!" };
  if (c.status === "signed" && c.terminated_on) {
    const ended = today ? c.terminated_on < today : false;
    return { label: ended ? t("Beendet durch Kündigung", "Ended by termination") : t("Gekündigt", "Terminated"), tone: ended ? "neutral" : "warning", symbol: ended ? "○" : "◷" };
  }
  if (ausgelaufen(c, today)) return { label: t("Ausgelaufen", "Expired"), tone: "neutral", symbol: "○" };
  if (c.status === "void" && c.void_kind) return { label: VOID_KIND_LABEL[c.void_kind], tone: "neutral", symbol: "○" };
  return STATUS_PILL[c.status];
}

export type SignerState = "missing" | "waiting" | "viewed" | "expired" | "portal" | "declined" | "signed" | "not_required" | "stopped" | "closed";

export interface SignerView {
  role: ContractRole;
  slot: number;
  state: SignerState;
  signer: SignerRow | null;
  label: string;
  pill: Pill;
  /** Link läuft in höchstens drei Tagen ab */
  expiresSoon: boolean;
}

const DAY = 86_400_000;

type StateContract = Pick<ContractListRow, "status" | "required_signers" | "declined_at" | "talent_id">;

export function signerState(c: StateContract, role: ContractRole, s: SignerRow | null, now: number, portalTalents?: ReadonlySet<string>): SignerState {
  if (s?.signed_at && !s.invalidated_at) return "signed";
  if (s?.decline_reason && s.invalidated_at) return "declined";
  if (!c.required_signers.includes(role) && !s) return "not_required";
  // Die Agentur-Zeile legt die Datenbank erst beim Versand an; sie unterschreibt im Portal
  if (role === "agency") return c.declined_at ? "stopped" : "waiting";
  if (!s || !s.email) return "missing";
  if (c.status === "draft") return "waiting";
  if (c.declined_at) return "stopped";
  // Creator mit Portalzugang unterschreiben im Portal; ihr Link „läuft“ nicht ab (Befund 27)
  const portal = role === "talent" && s.capacity === "party" && !!c.talent_id && !!portalTalents?.has(c.talent_id);
  if (s.token_expires_at && Date.parse(s.token_expires_at) < now) return portal ? "portal" : "expired";
  if (s.viewed_at) return "viewed";
  return portal ? "portal" : "waiting";
}

const STATE_PILL: Record<SignerState, (role: ContractRole) => Pill> = {
  missing: () => ({ label: t("Unterzeichner fehlt", "Signatory missing"), tone: "error", symbol: "!" }),
  waiting: (r) => ({ label: r === "agency" ? t("Gegenzeichnung offen", "Countersignature open") : t("Noch nicht geöffnet", "Not opened yet"), tone: "neutral", symbol: "○" }),
  viewed: () => ({ label: t("Geöffnet, nicht unterschrieben", "Opened, not signed"), tone: "info", symbol: "◇" }),
  expired: () => ({ label: t("Link abgelaufen", "Link expired"), tone: "error", symbol: "!" }),
  portal: () => ({ label: t("Im Portal offen", "Open in the portal"), tone: "info", symbol: "◇" }),
  declined: () => ({ label: t("Abgelehnt", "Declined"), tone: "error", symbol: "!" }),
  signed: () => ({ label: t("Unterschrieben", "Signed"), tone: "success", symbol: "✓" }),
  not_required: () => ({ label: t("Nicht nötig", "Not needed"), tone: "neutral", symbol: "○" }),
  stopped: () => ({ label: t("Angehalten", "On hold"), tone: "neutral", symbol: "○" }),
  closed: () => ({ label: t("Nicht unterschrieben", "Not signed"), tone: "neutral", symbol: "○" }),
};

export const CAPACITY_LABEL = { party: "", get legal_guardian() { return t("gesetzliche Vertretung", "legal representative"); } } as const;

/** Anzeigename einer Unterschriftszeile, z. B. „Marke 2“ oder „Creator · gesetzliche Vertretung“. */
export function signerLabel(role: ContractRole, slot: number, capacity: SignerRow["capacity"] | null, many: boolean): string {
  const base = `${ROLE_LABEL[role]}${many ? ` ${slot}` : ""}`;
  return capacity === "legal_guardian" ? t(`${base} · gesetzliche Vertretung`, `${base} · legal representative`) : base;
}

/** Eine Zeile je Unterzeichner; für verlangte Parteien ohne Zeile ein Platzhalter (fehlt bzw. Agentur offen). */
export function signerViews(c: Pick<ContractListRow, "id" | "status" | "required_signers" | "declined_at" | "talent_id" | "agency_signatures">, signers: readonly SignerRow[], now: number, portalTalents?: ReadonlySet<string>): SignerView[] {
  const mine = signers.filter((s) => s.contract_id === c.id);
  const roles = ROLE_ORDER.filter((r) => c.required_signers.includes(r) || mine.some((s) => s.role === r));
  const out: SignerView[] = [];
  for (const role of roles) {
    const rows = mine.filter((s) => s.role === role).sort((a, b) => a.slot - b.slot);
    const expected = role === "agency" && c.status === "draft" ? Math.max(c.agency_signatures ?? 1, rows.length) : rows.length;
    const count = Math.max(rows.length, expected, 1);
    for (let i = 0; i < count; i++) {
      const s = rows[i] ?? null;
      const slot = s?.slot ?? i + 1;
      // Aufgehoben: wer unterschrieben hatte, bleibt unterschrieben (der Vertrag galt bis dahin, Befund 6)
      const state: SignerState = c.status === "void" ? (s?.signed_at && !s.invalidated_at ? "signed" : "closed") : signerState(c, role, s, now, portalTalents);
      const exp = s?.token_expires_at ? Date.parse(s.token_expires_at) : NaN;
      out.push({
        role, slot, state, signer: s, label: signerLabel(role, slot, s?.capacity ?? null, count > 1), pill: STATE_PILL[state](role),
        expiresSoon: (state === "waiting" || state === "viewed") && Number.isFinite(exp) && exp - now < 3 * DAY && exp > now,
      });
    }
  }
  return out;
}

export type NextStep =
  | { kind: "edit"; label: string }
  | { kind: "upload"; label: string }
  | { kind: "send"; label: string }
  | { kind: "declined"; label: string; signerId: string | null }
  | { kind: "resend"; label: string; role: ContractRole; signerId: string | null; reason: "expired" }
  | { kind: "countersign"; label: string }
  | { kind: "wait"; label: string }
  | { kind: "final"; label: string }
  | { kind: "none"; label: string };

/**
 * Ist eine Partei fertig? Wie app.contract_role_complete (0059): „einer genügt“ bei einer Partei mit gesetzlicher Vertretung
 * heißt „eine Vertretung genügt“; der Creator selbst unterschreibt trotzdem und zählt nie allein (Befund 3).
 */
export function roleComplete(c: Pick<ContractListRow, "any_signer_roles">, views: readonly SignerView[], r: ContractRole): boolean {
  const vs = views.filter((v) => v.role === r);
  const done = (v: SignerView) => v.state === "signed";
  if ((c.any_signer_roles ?? []).includes(r)) {
    const guardians = vs.filter((v) => v.signer?.capacity === "legal_guardian");
    if (guardians.length) return guardians.some(done) && vs.filter((v) => v.signer?.capacity !== "legal_guardian").every((v) => done(v) || v.state === "not_required");
    return vs.some(done);
  }
  return vs.every((v) => done(v) || v.state === "not_required");
}

/** Haben alle Parteien außer der Agentur unterschrieben? (Die Agentur zeichnet danach gegen, Befund 13.) */
export function othersComplete(c: Pick<ContractListRow, "any_signer_roles">, views: readonly SignerView[]): boolean {
  return [...new Set(views.filter((v) => v.role !== "agency").map((v) => v.role))].every((r) => roleComplete(c, views, r));
}

/** Was als Nächstes zu tun ist (Reihenfolge: Ablehnung, abgelaufener Link, Gegenzeichnung, warten). */
export function nextStep(c: ContractListRow, views: readonly SignerView[]): NextStep {
  if (c.status === "void") return { kind: "none", label: t("Aufgehoben", "Rescinded") };
  if (c.status === "draft") return c.origin === "pdf_upload" ? { kind: "upload", label: t("PDF hochladen", "Upload PDF") } : { kind: "edit", label: t("Entwurf bearbeiten", "Edit draft") };
  if (c.status === "signed") {
    if (c.origin === "portal" && c.signed_pdf_path && ((c.required_signers.includes("brand") && !c.final_sent_to_brand_at) || (c.required_signers.includes("talent") && !c.final_sent_to_talent_at)))
      return { kind: "final", label: t("Fertiges PDF verschicken", "Send finished PDF") };
    return { kind: "none", label: t("Abgeschlossen", "Completed") };
  }
  const declined = views.find((v) => v.state === "declined");
  if (c.declined_at || declined) return { kind: "declined", label: t(`${declined?.label ?? t("Eine Partei", "A party")} hat abgelehnt: neue Fassung oder erneut senden`, `${declined?.label ?? t("Eine Partei", "A party")} declined: new version or send again`), signerId: declined?.signer?.id ?? null };
  const expired = views.find((v) => v.state === "expired");
  if (expired) return { kind: "resend", label: t(`${expired.label}: neuen Link senden`, `${expired.label}: send new link`), role: expired.role, signerId: expired.signer?.id ?? null, reason: "expired" };
  const agency = views.filter((v) => v.role === "agency");
  if (agency.some((a) => a.state !== "signed") && othersComplete(c, views)) return { kind: "countersign", label: t("Gegenzeichnen", "Countersign") };
  return { kind: "wait", label: t("Warten auf Unterschrift", "Waiting for signature") };
}

/** Rang für die Sortierung „Dringendes zuerst“ (kleiner = dringender). */
export function urgency(step: NextStep, c: ContractListRow): number {
  switch (step.kind) {
    case "declined": return 0;
    case "upload": return 0;
    case "resend": return 1;
    case "countersign": return 2;
    case "final": return 3;
    case "wait": return 4;
    case "edit": return 5;
    default: return c.status === "signed" ? 6 : 7;
  }
}

export type ListFilter = "offen" | "entwuerfe" | "unterschrift" | "gegenzeichnen" | "handeln" | "abgeschlossen" | "aufgehoben" | "alle";

export const FILTER_LABEL: Record<ListFilter, string> = {
  get offen() { return t("Offen", "Open"); }, get handeln() { return t("Handlung nötig", "Action needed"); }, get entwuerfe() { return t("Entwürfe", "drafts"); }, get unterschrift() { return t("Zur Unterschrift", "Awaiting signature"); }, get gegenzeichnen() { return t("Gegenzeichnen", "Countersign"); },
  get abgeschlossen() { return t("Abgeschlossen", "Completed"); }, get aufgehoben() { return t("Aufgehoben", "Rescinded"); }, get alle() { return t("Alle", "All"); },
};

export function matchesFilter(f: ListFilter, c: ContractListRow, step: NextStep): boolean {
  switch (f) {
    case "offen": return c.status === "draft" || c.status === "sent" || c.status === "partially_signed";
    case "handeln": return step.kind === "resend" || step.kind === "declined" || step.kind === "countersign" || step.kind === "final" || step.kind === "upload";
    case "entwuerfe": return c.status === "draft";
    case "unterschrift": return c.status === "sent" || c.status === "partially_signed";
    case "gegenzeichnen": return step.kind === "countersign";
    case "abgeschlossen": return c.status === "signed";
    case "aufgehoben": return c.status === "void";
    default: return true;
  }
}

export interface ListKpis {
  drafts: number;
  waiting: number;
  expired: number;
  declined: number;
  countersign: number;
  signedThisMonth: number;
  sentThisMonth: number;
}

export function listKpis(rows: readonly { c: ContractListRow; views: SignerView[]; step: NextStep }[], monthPrefix: string): ListKpis {
  const k: ListKpis = { drafts: 0, waiting: 0, expired: 0, declined: 0, countersign: 0, signedThisMonth: 0, sentThisMonth: 0 };
  for (const r of rows) {
    if (r.c.status === "draft") k.drafts += 1;
    if (r.c.status === "sent" || r.c.status === "partially_signed") k.waiting += 1;
    if (r.c.status !== "void" && !r.c.declined_at && r.views.some((v) => v.state === "expired")) k.expired += 1;
    if (r.step.kind === "declined") k.declined += 1;
    if (r.step.kind === "countersign") k.countersign += 1;
    if (r.c.status === "signed" && (r.c.completed_at ?? "").startsWith(monthPrefix)) k.signedThisMonth += 1;
    if (r.c.origin === "portal" && (r.c.sent_at ?? "").startsWith(monthPrefix)) k.sentThisMonth += 1;
  }
  return k;
}

/** Frühester Ablauf eines noch offenen Links (für die Spalte „Link gültig bis“); Creator mit Portalzugang zählen nicht. */
export function openLinkExpiry(views: readonly SignerView[]): string | null {
  const open = views.filter((v) => v.role !== "agency" && (v.state === "waiting" || v.state === "viewed" || v.state === "expired") && v.signer?.token_expires_at);
  const dates = open.map((v) => v.signer!.token_expires_at!).sort();
  return dates[0] ?? null;
}

/** Platzhalter im Entwurf, solange die Nummer noch nicht vergeben ist (sie entsteht beim Versand, Befund 26). */
export const NUMBER_PREVIEW: Record<"de" | "en", string> = { de: "(wird beim Versand vergeben)", en: "(assigned on sending)" };

/** Angezeigte Vertragsnummer: vergebene Nummer der Agentur, sonst die frühere Form, sonst „–“ (Nummer entsteht beim Versand). */
export function contractNumberLabel(n: number | null, label?: string | null): string {
  if (label) return label;
  return n != null ? `V-${String(n).padStart(4, "0")}` : "–";
}

/** Beispielnummer eines Formats für die Einstellungen („NL-{YYYY}-{NNN}“ → „NL-2026-001“). */
export function numberFormatExample(format: string, year: number, n = 1): string {
  const digits = /\{(N{1,8})\}/.exec(format)?.[1]?.length ?? 4;
  return format.replace("{YYYY}", String(year)).replace("{YY}", String(year).slice(-2)).replace(/\{N{1,8}\}/, String(n).padStart(digits, "0"));
}

export const EVENT_LABEL: Record<string, string> = {
  get sent() { return t("Link versendet", "Link sent"); },
  get viewed() { return t("Geöffnet", "Opened"); },
  get filled() { return t("Angaben eingetragen", "Details filled in"); },
  get signed() { return t("Unterschrieben", "Signed"); },
  get declined() { return t("Abgelehnt", "Declined"); },
  get resent() { return t("Neuer Link versendet", "New link sent"); },
  get countersigned() { return t("Von der Agentur gegengezeichnet", "Countersigned by the agency"); },
  get completed() { return t("Vollständig unterschrieben", "Fully signed"); },
  get voided() { return t("Aufgehoben", "Rescinded"); },
  get external_uploaded() { return t("Extern unterschriebenes PDF abgelegt", "Externally signed PDF filed"); },
  get final_sent() { return t("Fertiges PDF verschickt", "Finished PDF sent"); },
  get recipient_changed() { return t("Empfänger geändert", "Recipient changed"); },
  get link_shared() { return t("Link manuell geteilt", "Link shared manually"); },
  get reminded() { return t("Erinnerung mit neuem Link versendet", "Reminder with new link sent"); },
  get copy_sent() { return t("Ausfertigung mit Leselink versendet", "Executed copy with read link sent"); },
  get read() { return t("Ausfertigung gelesen", "Executed copy read"); },
  get resumed() { return t("Wieder freigegeben", "Released again"); },
  get terminated() { return t("Kündigung vermerkt", "Termination recorded"); },
  get renewed() { return t("Automatisch verlängert", "Renewed automatically"); },
  get final_uploaded() { return t("Fertiges PDF abgelegt", "Finished PDF filed"); },
  get deal_relinked() { return t("Kooperation geändert", "Deal changed"); },
  get final_replaced() { return t("Fertiges PDF ersetzt", "Finished PDF replaced"); },
  get version_detached() { return t("Als eigenständiger Vertrag geführt", "Treated as an independent contract"); },
  get document_read() { return t("Dokument der Marke gelesen (bestätigt)", "Brand's document read (confirmed)"); },
  get term_changed() { return t("Laufzeit geändert", "Term changed"); },
  get termination_corrected() { return t("Kündigung berichtigt", "Termination corrected"); },
  get amendment_moved() { return t("Nachtrag umgehängt", "Addendum moved"); },
};

/** Kalendertag eines Zeitpunkts in einer Zeitzone als 'YYYY-MM-DD' (wie app.local_day). */
export function localDay(iso: string | null | undefined, timeZone: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
}

/**
 * Parteien eines Vertrags (wie app.contract_parties, VERTRAEGE-07): die unterschreibenden; bei extern abgelegten Verträgen
 * die Agentur und, laut Zuordnung, Creator und Marke. Nur sie können eine Kündigung erklärt haben.
 */
export function contractParties(c: { origin: string | null; required_signers: readonly ContractRole[]; talent_id: string | null; brand_id: string | null }): ContractRole[] {
  if (c.origin === "pdf_upload" || c.required_signers.length === 0) {
    return ["agency", ...(c.talent_id ? ["talent" as const] : []), ...(c.brand_id ? ["brand" as const] : [])];
  }
  return (["agency", "brand", "talent"] as ContractRole[]).filter((r) => c.required_signers.includes(r));
}

/**
 * Wer bekommt eine Mitteilung an die Parteien (Kündigung, Aufhebung)? Wie app.contract_inform_parties: per Mail jede
 * eingeladene Person außer der Agentur (der Creator selbst über das Portal), der Creator, wenn er den Vertrag sieht.
 * Parteien ohne Weg ins Portal (etwa die Marke eines extern abgelegten Vertrags) nennt der Text ausdrücklich (VERTRAEGE-07).
 */
/** Erreicht eine Mitteilung über das Portal überhaupt jemanden? (K-113: sonst darf das Ergebnis nicht „informiert“ sagen) */
export function mitteilungErreichtJemanden(c: Parameters<typeof mitteilungEmpfaenger>[0], signers: Parameters<typeof mitteilungEmpfaenger>[1]): boolean {
  const talentSees = c.visible_to_talent ?? (c.required_signers.includes("talent") || c.origin === "pdf_upload");
  if (c.talent_id && talentSees) return true;
  return signers.some((s) => s.role !== "agency" && !!(s.email ?? "").trim() && !!s.sent_at && !(s.role === "talent" && s.capacity === "party" && c.talent_id));
}

export function mitteilungEmpfaenger(c: { origin: string | null; required_signers: readonly ContractRole[]; talent_id: string | null; brand_id: string | null; visible_to_talent: boolean | null },
  signers: readonly { role: ContractRole; capacity: "party" | "legal_guardian"; email: string | null; sent_at: string | null }[]): string {
  const to = new Set<string>();
  const reached = new Set<ContractRole>();
  for (const s of signers) {
    if (s.role === "agency" || !(s.email ?? "").trim() || !s.sent_at || (s.role === "talent" && s.capacity === "party" && c.talent_id)) continue;
    to.add(s.capacity === "legal_guardian" ? t("die gesetzliche Vertretung", "the legal representative") : s.role === "brand" ? t("die Marke", "the brand") : t("den Creator", "the creator"));
    reached.add(s.role);
  }
  const talentSees = c.visible_to_talent ?? (c.required_signers.includes("talent") || c.origin === "pdf_upload");
  if (c.talent_id && talentSees) { to.add(t("den Creator", "the creator")); reached.add("talent"); }
  const missing = contractParties(c).filter((r) => r !== "agency" && !reached.has(r));
  const list = [...to];
  const join = (xs: string[]) => (xs.length > 1 ? `${xs.slice(0, -1).join(", ")} ${t("und", "and")} ${xs[xs.length - 1]}` : xs[0] ?? "");
  const head = list.length
    ? t(`Die Mitteilung geht an ${join(list)}; die interne Notiz nie.`, `The message goes to ${join(list)}; the internal note never does.`)
    : t("Über das Portal erreicht die Mitteilung niemanden; die interne Notiz bleibt intern.", "Via the portal the message reaches nobody; the internal note stays internal.");
  const miss = missing.map((r) => (r === "brand" ? t("die Marke", "the brand") : t("den Creator", "the creator")));
  if (!miss.length) return head;
  const wer = join(miss);
  return t(`${head} ${wer.replace(/^d/, "D")} erreichst du so nicht: bitte selbst informieren.`, `${head} You cannot reach ${wer} this way: please inform them yourself.`);
}

const TERM_FIELD: Record<string, string> = {
  get starts_on() { return t("Beginn", "Start"); }, get ends_on() { return t("Ende", "End"); }, get auto_renew() { return t("automatische Verlängerung", "automatic renewal"); },
  get renewal_months() { return t("Verlängerung in Monaten", "Renewal in months"); }, get notice_days() { return t("Kündigungsfrist in Tagen", "Notice period in days"); },
};
const termValue = (v: unknown) => (v === null || v === undefined ? "–" : v === true ? t("ja", "yes") : v === false ? t("nein", "no")
  : typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) ? (currentLang() === "en" ? v.split("-").reverse().join("/") : v.split("-").reverse().join(".")) : String(v));

/** Laufzeitänderung aus der Beweiskette (Ereignis term_changed, VERTRAEGE-06): nur geänderte Felder, alt → neu. */
export function termChangeText(details: Record<string, unknown> | null | undefined): string {
  const from = (details?.["from"] ?? {}) as Record<string, unknown>;
  const to = (details?.["to"] ?? {}) as Record<string, unknown>;
  return Object.keys(TERM_FIELD).filter((k) => termValue(from[k]) !== termValue(to[k]))
    .map((k) => `${TERM_FIELD[k]} ${termValue(from[k])} → ${termValue(to[k])}`).join(", ");
}

/** Berichtigte Kündigung aus der Beweiskette (termination_corrected, VERTRAEGE-R2-02): Enddatum und Zugang alt → neu, Grund. */
export function terminationCorrectionText(details: Record<string, unknown> | null | undefined): string {
  const from = (details?.["from"] ?? {}) as Record<string, unknown>;
  const to = (details?.["to"] ?? {}) as Record<string, unknown>;
  const parts: string[] = [];
  if (termValue(from["effective"]) !== termValue(to["effective"])) parts.push(t(`Ende ${termValue(from["effective"])} → ${termValue(to["effective"])}`, `End ${termValue(from["effective"])} → ${termValue(to["effective"])}`));
  if (termValue(from["received_on"]) !== termValue(to["received_on"])) parts.push(t(`Zugang ${termValue(from["received_on"])} → ${termValue(to["received_on"])}`, `Received ${termValue(from["received_on"])} → ${termValue(to["received_on"])}`));
  if (details?.["extraordinary"] === true) parts.push(t("außerordentlich", "extraordinary"));
  if (typeof details?.["reason"] === "string") parts.push(t(`Grund: ${details["reason"]}`, `Reason: ${details["reason"]}`));
  return parts.join(" · ");
}

/** Umgehängter Nachtrag (amendment_moved, VERTRAEGE-R2-04): am Nachtrag „von → nach“, an der neuen Fassung der Nachtrag. */
export function amendmentMovedText(details: Record<string, unknown> | null | undefined): string {
  const s = (k: string) => (typeof details?.[k] === "string" ? String(details[k]) : "–");
  return details?.["amendment_label"]
    ? t(`Nachtrag ${s("amendment_label")} gilt jetzt zu dieser Fassung (vorher zu ${s("from_label")})`, `Addendum ${s("amendment_label")} now applies to this version (previously to ${s("from_label")})`)
    : t(`gilt jetzt zu ${s("to_label")} statt zu ${s("from_label")} (neue Fassung)`, `now applies to ${s("to_label")} instead of ${s("from_label")} (new version)`);
}

/**
 * Hinweis zur vermerkten Kündigung (VERTRAEGE-R2-08): Die Parteien erfahren die Kündigung immer (Vertrag und Enddatum);
 * ohne eigene Mitteilung heißt das nicht, dass niemand informiert wurde.
 */
export function kuendigungNotiz(c: { termination_reason: string | null; termination_message: string | null }): string | null {
  if (!c.termination_reason) return null;
  return t(`Interne Notiz zur Kündigung: ${c.termination_reason}`, `Internal note on the termination: ${c.termination_reason}`)
    + (c.termination_message
      ? t(` · An die Parteien: „${c.termination_message}“`, ` · To the parties: “${c.termination_message}”`)
      : t(" · Ohne eigene Mitteilung: Wer über das Portal erreichbar ist, wurde über Kündigung und Enddatum informiert.", " · Without a personal message: whoever can be reached via the portal was informed of the termination and end date."));
}

/**
 * Frühestes Enddatum einer Kündigung (VERTRAEGE-R2-02, wie app.contract_termination_dates_check): nicht vor dem Beginn und
 * nicht vor dem Zugang; der Zugang nicht vor dem Vertragsschluss. `concluded` ist der Tag des Vertragsschlusses.
 */
export function kuendigungGrenzen(c: { starts_on: string | null }, concluded: string | null, received: string): { minEffective: string; receivedTooEarly: boolean } {
  const days = [received, c.starts_on, concluded].filter((x): x is string => !!x).map((x) => x.slice(0, 10)).sort();
  return { minEffective: days[days.length - 1]!, receivedTooEarly: !!concluded && received < concluded.slice(0, 10) };
}

/** Tag des Vertragsschlusses in der Anzeige (wie app.contract_concluded_on): extern das Unterschriftsdatum, sonst die späteste Unterschrift (Papierdatum), sonst der Abschluss. */
export function abschlussTag(c: { origin: string | null; external_signed_on: string | null; completed_at: string | null },
  signers: readonly { signed_at: string | null; invalidated_at: string | null; paper_signed_on?: string | null }[], localDayOf: (ts: string) => string): string | null {
  if (c.origin === "pdf_upload") return c.external_signed_on ?? (c.completed_at ? localDayOf(c.completed_at) : null);
  const days = signers.filter((s) => s.signed_at && !s.invalidated_at).map((s) => s.paper_signed_on ?? localDayOf(s.signed_at!)).sort();
  return days[days.length - 1] ?? (c.completed_at ? localDayOf(c.completed_at) : null);
}

/**
 * Beim Ersetzen des abgelegten PDFs eine neue Fassung für den Creator anbieten (VERTRAEGE-R2-03, wie
 * contract_replace_final_pdf): bei Portalverträgen nur, wenn die Anlage schon bei der Unterschrift vor ihm verborgen war;
 * bei extern abgelegten Verträgen, wenn es schon eine Creator-Fassung gibt.
 */
export function replaceCreatorAllowed(c: { origin: string | null; annex_hidden_from: readonly ContractRole[]; signed_pdf_path_talent: string | null }, annexHasContent: boolean): boolean {
  return c.origin === "portal" ? c.annex_hidden_from.includes("talent") && annexHasContent : !!c.signed_pdf_path_talent;
}

/**
 * Schritte im Fortschritt (VERTRAEGE-R2-09): eine Papierunterschrift steht mit dem Datum auf dem Papier, nicht mit dem Tag
 * der Erfassung (wie Unterzeichnerliste, Protokoll und Ausfertigung).
 */
export function fortschrittSchritte(c: { origin: string | null; status: ContractStatus; created_at: string; sent_at: string | null; external_signed_on: string | null; completed_at: string | null; voided_at: string | null },
  views: readonly Pick<SignerView, "label" | "state" | "signer">[]): { label: string; at: string | null }[] {
  return [
    { label: t("Erstellt", "Created"), at: c.created_at },
    { label: c.origin === "pdf_upload" ? t("Extern unterschrieben", "Signed externally") : t("Versendet", "Sent"), at: c.origin === "pdf_upload" ? c.external_signed_on : c.sent_at },
    ...(c.origin === "pdf_upload" ? [] : views.map((v) => ({ label: t(`${v.label} unterschrieben`, `${v.label} signed`),
      at: v.state === "signed" ? v.signer?.paper_signed_on ?? v.signer?.signed_at ?? null : null }))),
    ...(c.status === "void" && c.completed_at ? [{ label: t("Abgeschlossen", "Completed"), at: c.completed_at }] : []),
    { label: c.status === "void" ? t("Aufgehoben", "Rescinded") : t("Abgeschlossen", "Completed"), at: c.status === "void" ? c.voided_at : c.completed_at },
  ];
}

/** Änderung der Kooperation aus der Beweiskette (deal_relinked); „weitere Kooperation“ bei Rahmenverträgen (VERTRAEGE-01). */
export function relinkText(details: Record<string, unknown> | null | undefined): string {
  const d = details ?? {};
  const title = (k: string) => (typeof d[k] === "string" ? String(d[k]) : "–");
  return `${d["how"] === "link" ? t("weitere Kooperation: ", "additional deal: ") : ""}${title("from_title")} → ${title("to_title")}${typeof d["reason"] === "string" ? `: ${d["reason"]}` : ""}`;
}

/**
 * Laufzeit in Worten (VERTRAEGE-06, -08): gekündigt geht vor; „Kündigung spätestens am“ nur, wenn sich der Vertrag sonst
 * automatisch verlängert (ein befristeter Vertrag endet von selbst). Mit `today` steht ein vergangenes Ende in der
 * Vergangenheit (VERTRAEGE-R3-01).
 */
export function laufzeitText(c: { starts_on: string | null; ends_on: string | null; auto_renew: boolean; renewal_months: number | null; notice_days: number | null;
  terminated_on: string | null; term_months: number | null; status?: ContractStatus }, fmt: (day: string) => string, today?: string | null): string | null {
  const vorbei = (day: string) => !!today && day.slice(0, 10) < today.slice(0, 10);
  if (c.terminated_on && vorbei(c.terminated_on)) {
    return t(`Durch Kündigung beendet am ${fmt(c.terminated_on)}.${c.starts_on ? ` Laufzeit vom ${fmt(c.starts_on)} bis ${fmt(c.terminated_on)}.` : ""}`,
      `Ended by termination on ${fmt(c.terminated_on)}.${c.starts_on ? ` Term from ${fmt(c.starts_on)} to ${fmt(c.terminated_on)}.` : ""}`);
  }
  if (c.terminated_on) {
    return t(`Gekündigt zum ${fmt(c.terminated_on)}: Der Vertrag endet an diesem Tag, eine Verlängerung entfällt.${c.starts_on ? ` Laufzeit seit ${fmt(c.starts_on)}.` : ""}`,
      `Terminated effective ${fmt(c.terminated_on)}: the contract ends on this day, a renewal no longer applies.${c.starts_on ? ` Term since ${fmt(c.starts_on)}.` : ""}`);
  }
  if (c.term_months && !c.starts_on) return t(`Laufzeit ${c.term_months} Monate ab der letzten Unterschrift; Beginn und Ende entstehen beim Abschluss.`, `Term ${c.term_months} months from the last signature; start and end are set on conclusion.`);
  if (!c.ends_on) return c.starts_on ? t(`Unbefristet seit ${fmt(c.starts_on)}.`, `Open-ended since ${fmt(c.starts_on)}.`) : null;
  const span = t(`Laufzeit ${c.starts_on ? `vom ${fmt(c.starts_on)} ` : ""}bis ${fmt(c.ends_on)}`, `Term ${c.starts_on ? `from ${fmt(c.starts_on)} ` : ""}until ${fmt(c.ends_on)}`);
  if (!c.auto_renew && vorbei(c.ends_on)) {
    return t(`Ausgelaufen am ${fmt(c.ends_on)} ohne Verlängerung; einer Kündigung bedurfte es nicht. Laufzeit ${c.starts_on ? `vom ${fmt(c.starts_on)} ` : ""}bis ${fmt(c.ends_on)}.`,
      `Expired on ${fmt(c.ends_on)} without renewal; no termination was needed. Term ${c.starts_on ? `from ${fmt(c.starts_on)} ` : ""}until ${fmt(c.ends_on)}.`);
  }
  if (!c.auto_renew) return t(`Endet ohne Verlängerung. ${span}; eine Kündigung ist dafür nicht nötig.`, `Ends without renewal. ${span}; no termination is needed for this.`);
  const e = new Date(`${c.ends_on}T00:00:00Z`);
  e.setUTCDate(e.getUTCDate() - (c.notice_days ?? 0));
  const spaetestens = fmt(e.toISOString().slice(0, 10));
  return t(`Verlängert sich automatisch um ${c.renewal_months ?? 12} Monate, wenn nicht gekündigt wird. ${span}, Kündigung spätestens am ${spaetestens}.`,
    `Renews automatically by ${c.renewal_months ?? 12} months unless terminated. ${span}, termination by ${spaetestens} at the latest.`);
}

/** SHA-256 als Hex, wie app.sha256_hex in der Datenbank (UTF-8). */
export async function sha256Hex(text: string): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/** Prüft, ob der aktuelle Text dem zuletzt protokollierten Stand entspricht (Manipulationswarnung). */
export async function textIntegrity(html: string | null, annex: string | null, events: readonly { event: string; document_sha256: string | null }[], documentSha: string | null): Promise<"ok" | "mismatch" | "unknown"> {
  if (!html) return "unknown";
  const stamped = [...events].reverse().find((e) => e.document_sha256 && ["sent", "filled", "signed", "countersigned"].includes(e.event))?.document_sha256 ?? documentSha;
  if (!stamped) return "unknown";
  return (await sha256Hex(html + (annex ?? ""))) === stamped ? "ok" : "mismatch";
}

/**
 * Abschluss-Hash nachrechnen (gleiches Verfahren wie app.contract_final_hash). Gezählt werden die Unterschriften, die beim
 * Abschluss gültig waren; ein späteres Aufheben macht sie ungültig, ändert den Hash aber nicht.
 */
export async function finalIntegrity(html: string | null, annex: string | null, signers: readonly SignerRow[], finalSha: string | null, completedAt: string | null): Promise<"ok" | "mismatch" | "unknown"> {
  if (!finalSha || !completedAt) return "unknown";
  const done = Date.parse(completedAt);
  const input = finalHashInput(html, annex, signers.map((s) => ({
    role: s.role, slot: s.slot, signing_name: s.signing_name, signed_at: s.signed_at,
    valid: !!s.signed_at && (!s.invalidated_at || Date.parse(s.invalidated_at) > done),
  })));
  return (await sha256Hex(input)) === finalSha ? "ok" : "mismatch";
}

/**
 * Hinweis an einem Nachtrag nach dem Stand des Ursprungsvertrags (VERTRAEGE-04): gilt weiter, gekündigt, ersetzt oder
 * aufgehoben. Ohne gültigen Ursprung lässt sich ein Nachtrag nicht mehr senden; offene enden mit dem Ursprung.
 */
export function nachtragHinweis(original: { status: ContractStatus; void_kind?: VoidKind | null; terminated_on?: string | null },
  self: ContractStatus, fmt: (day: string) => string): string {
  if (original.status === "signed") {
    return original.terminated_on ? t(`der Ursprungsvertrag ist gekündigt zum ${fmt(original.terminated_on)}; dieser Nachtrag endet spätestens mit ihm.`, `the original contract is terminated effective ${fmt(original.terminated_on)}; this addendum ends with it at the latest.`) : t("der Ursprungsvertrag gilt weiter; dieser Nachtrag ergänzt ihn.", "the original contract continues to apply; this addendum supplements it.");
  }
  if (original.status === "void" && original.void_kind === "superseded") {
    return self === "signed" ? t("der Ursprungsvertrag ist durch eine neue Fassung ersetzt; dieser Nachtrag gilt für das fortbestehende Vertragsverhältnis weiter. Bitte gegen die neue Fassung prüfen.", "the original contract has been replaced by a new version; this addendum continues to apply to the ongoing contractual relationship. Please check it against the new version.")
      : t("der Ursprungsvertrag ist durch eine neue Fassung ersetzt. Für die neue Fassung bitte einen neuen Nachtrag anlegen.", "the original contract has been replaced by a new version. Please create a new addendum for the new version.");
  }
  if (original.status === "void") {
    return self === "draft" ? t("der Ursprungsvertrag ist aufgehoben; dieser Nachtrag ist gegenstandslos und lässt sich nicht mehr senden.", "the original contract has been rescinded; this addendum is void and can no longer be sent.") : t("der Ursprungsvertrag ist aufgehoben; dieser Nachtrag ist damit gegenstandslos.", "the original contract has been rescinded; this addendum is therefore void.");
  }
  return t("der Ursprungsvertrag ist noch nicht unterschrieben.", "the original contract has not been signed yet.");
}
